import api from '../lib/axios';
import { tokenStorage } from '../utils/tokenStorage';
import { guestSessionManager } from '../utils/guestSession';
import i18n from 'i18next';
import { AxiosError } from 'axios';
import { logger } from '../utils/logger';

// API Endpoints from environment variables
const API_ENDPOINTS = {
  CART_ADD: import.meta.env.VITE_API_V1_CART_ADD,
  CART_GET: import.meta.env.VITE_API_V1_CART_GET,
  CART_UPDATE: import.meta.env.VITE_API_V1_CART_UPDATE,
  CART_REMOVE: import.meta.env.VITE_API_V1_CART_REMOVE,
  CART_CLEAR: import.meta.env.VITE_API_V1_CART_CLEAR,
};

export interface CartItem {
  productId: string;
  quantity: number;
  price: number;
  name: {
    en: string;
    my: string;
  };
  image: string;
  addedAt: string;
}

export interface Cart {
  _id: string;
  userId: string;
  items: CartItem[];
  totalAmount: number;
  totalItems: number;
  createdAt: string;
  updatedAt: string;
}

export interface CartResponse {
  success: boolean;
  message: string;
  data: Cart;
}

export interface AddToCartRequest {
  productId: string;
  quantity: number;
}

export interface UpdateCartRequest {
  productId: string;
  quantity: number;
}

// Request deduplication to prevent duplicate API calls
class RequestDeduplicator {
  private pendingRequests = new Map<string, Promise<unknown>>();
  private requestTimestamps = new Map<string, number>();
  private readonly DEDUP_WINDOW = 1000; // 1 second deduplication window

  async deduplicate<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    const now = Date.now();
    const lastRequestTime = this.requestTimestamps.get(key) || 0;

    // If there's a pending request for the same key, return it
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)! as Promise<T>;
    }

    // If request is within deduplication window, reject
    if (now - lastRequestTime < this.DEDUP_WINDOW) {
      throw new Error('Request too frequent, please wait a moment');
    }

    // Create new request
    const promise = requestFn();
    this.pendingRequests.set(key, promise);
    this.requestTimestamps.set(key, now);

    // Clean up after request completes
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });

    return promise;
  }

  clear() {
    this.pendingRequests.clear();
    this.requestTimestamps.clear();
  }
}

const requestDeduplicator = new RequestDeduplicator();

class CartService {
  private async getHeaders() {
    const token = tokenStorage.getAccessToken();
    const currentLanguage = i18n.language || 'my';

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept-Language': currentLanguage,
    };

    if (token) {
      // Authenticated user
      headers.Authorization = `Bearer ${token}`;
    } else {
      // Guest user - get session ID if available
      const sessionId = await guestSessionManager.getSession();
      logger.log('🔍 CartService getHeaders - Session ID:', sessionId);

      // Only add gid header if we have a session ID
      if (sessionId) {
        headers.gid = sessionId;
      } else {
        logger.log('🔍 No gid available - backend will set one on first API call');
      }
    }

    return headers;
  }

  async addToCart(request: AddToCartRequest): Promise<CartResponse> {
    // Create deduplication key based on product ID and quantity
    const dedupKey = `addToCart_${request.productId}_${request.quantity}`;

    return requestDeduplicator.deduplicate(dedupKey, async () => {
      try {
        const headers = await this.getHeaders();
        logger.log('🔍 CartService addToCart - Request:', request);
        logger.log('🔍 CartService addToCart - Headers:', headers);
        logger.log('🔍 CartService addToCart - API Endpoint:', API_ENDPOINTS.CART_ADD);

        const response = await api.post(API_ENDPOINTS.CART_ADD, request, {
          headers,
        });

        logger.log('🔍 CartService addToCart - Response status:', response.status);
        logger.log('🔍 CartService addToCart - Response data:', response.data);

        return response.data;
      } catch (error: unknown) {
        logger.error('🔍 CartService addToCart - Error:', error);
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as AxiosError;
          logger.error('🔍 CartService addToCart - Error response:', axiosError.response?.data);
          logger.error('🔍 CartService addToCart - Error status:', axiosError.response?.status);
        }

        const errorMessage =
          error instanceof Error && 'response' in error
            ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
            : 'Failed to add to cart';
        throw new Error(errorMessage);
      }
    });
  }

  async updateCartItem(request: UpdateCartRequest): Promise<CartResponse> {
    try {
      const headers = await this.getHeaders();
      const response = await api.put(API_ENDPOINTS.CART_UPDATE, request, {
        headers,
      });
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to update cart item';
      throw new Error(errorMessage);
    }
  }

  async removeFromCart(productId: string): Promise<CartResponse> {
    try {
      const headers = await this.getHeaders();
      const response = await api.delete(`${API_ENDPOINTS.CART_REMOVE}/${productId}`, {
        headers,
      });
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to remove item from cart';
      throw new Error(errorMessage);
    }
  }

  async clearCart(): Promise<CartResponse> {
    try {
      const headers = await this.getHeaders();
      const response = await api.delete(API_ENDPOINTS.CART_CLEAR, {
        headers,
      });
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to clear cart';
      throw new Error(errorMessage);
    }
  }

  async getCart(): Promise<CartResponse> {
    try {
      const headers = await this.getHeaders();
      logger.log('🔍 CartService getCart - Headers:', headers);
      logger.log('🔍 CartService getCart - API Endpoint:', API_ENDPOINTS.CART_GET);

      const response = await api.get(API_ENDPOINTS.CART_GET, {
        headers,
      });

      logger.log('🔍 CartService getCart - Response status:', response.status);
      logger.log('🔍 CartService getCart - Response data:', response.data);

      return response.data;
    } catch (error: unknown) {
      logger.error('🔍 CartService getCart - Error:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError;
        logger.error('🔍 CartService getCart - Error response:', axiosError.response?.data);
        logger.error('🔍 CartService getCart - Error status:', axiosError.response?.status);
      }

      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to get cart';
      throw new Error(errorMessage);
    }
  }
}

export const cartService = new CartService();
